import React, { ReactNode, ElementType, forwardRef } from 'react'
import styled from '@emotion/styled'

// This list is a work in progress, commented out items haven't been implemented yet.
import { BoxComponentProps, BoxStyleProps } from './Box.types'

import {
  resolveNegativeMarginLeft,
  resolveNegativeMarginTop,
  resolveToPxIfUnitless,
  resolveFlexJustify,
  resolveFlexAlign
} from './BoxUtils'
import useResponsiveProp from '../../hooks/useResponsiveProp'
import { useScreenSize } from '../../hooks/useScreenSize'

export interface BoxProps
  extends BoxComponentProps,
    Omit<BaseProps, 'hidden' | 'as' | 'width' | 'height'> {
  children?: ReactNode
  component?: ElementType | keyof JSX.IntrinsicElements
}

const BoxComponent = forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      component = 'div',
      background,
      display,
      position,
      hidden,
      height,
      minHeight,
      maxHeight,
      width,
      minWidth,
      maxWidth,
      radius,
      flexDirection,
      flexWrap,
      justifyContent,
      alignItems,
      flexGrow,
      inset,
      insetSquish,
      insetStretch,
      paddingX,
      paddingY,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      overflow,
      top,
      bottom,
      right,
      left,
      transform,
      ...restProps
    },
    forwardedRef
  ) => {
    const device = useScreenSize()

    const responsiveBackground = useResponsiveProp(background, device)
    const responsiveDisplay = useResponsiveProp(display, device)
    const responsivePosition = useResponsiveProp(position, device)
    const responsiveHidden = useResponsiveProp(hidden, device)
    const responsiveHeight = useResponsiveProp(height, device)
    const responsiveMinHeight = useResponsiveProp(minHeight, device)
    const responsiveMaxHeight = useResponsiveProp(maxHeight, device)
    const responsiveWidth = useResponsiveProp(width, device)
    const responsiveMinWidth = useResponsiveProp(minWidth, device)
    const responsiveMaxWidth = useResponsiveProp(maxWidth, device)
    const responsiveRadius = useResponsiveProp(radius, device)
    const responsiveFlexDirection = useResponsiveProp(flexDirection, device)
    const responsiveFlexWrap = useResponsiveProp(flexWrap, device)
    const responsiveJustifyContent = useResponsiveProp(justifyContent, device)
    const responsiveAlignItems = useResponsiveProp(alignItems, device)
    const responsiveFlexGrow = useResponsiveProp(flexGrow, device)
    const responsiveInset = useResponsiveProp(inset, device)
    const responsiveInsetSquish = useResponsiveProp(insetSquish, device)
    const responsiveInsetStretch = useResponsiveProp(insetStretch, device)
    const responsivePaddingX = useResponsiveProp(paddingX, device)
    const responsivePaddingY = useResponsiveProp(paddingY, device)
    const responsivePaddingTop = useResponsiveProp(paddingTop, device)
    const responsivePaddingBottom = useResponsiveProp(paddingBottom, device)
    const responsivePaddingLeft = useResponsiveProp(paddingLeft, device)
    const responsivePaddingRight = useResponsiveProp(paddingRight, device)
    const responsiveMargin = useResponsiveProp(margin, device)
    const responsiveMarginX = useResponsiveProp(marginX, device)
    const responsiveMarginY = useResponsiveProp(marginY, device)
    const responsiveMarginTop = useResponsiveProp(marginTop, device)
    const responsiveMarginBottom = useResponsiveProp(marginBottom, device)
    const responsiveMarginLeft = useResponsiveProp(marginLeft, device)
    const responsiveMarginRight = useResponsiveProp(marginRight, device)
    const responsiveOverflow = useResponsiveProp(overflow, device)
    const responsiveTop = useResponsiveProp(top, device)
    const responsiveBottom = useResponsiveProp(bottom, device)
    const responsiveRight = useResponsiveProp(right, device)
    const responsiveLeft = useResponsiveProp(left, device)
    const responsiveTransform = useResponsiveProp(transform, device)

    return (
      <StyledDiv
        // @ts-ignore
        ref={forwardedRef}
        as={component}
        background={responsiveBackground}
        display={responsiveDisplay}
        position={responsivePosition}
        hidden={responsiveHidden}
        height={responsiveHeight}
        minHeight={responsiveMinHeight}
        maxHeight={responsiveMaxHeight}
        width={responsiveWidth}
        minWidth={responsiveMinWidth}
        maxWidth={responsiveMaxWidth}
        radius={responsiveRadius}
        flexDirection={responsiveFlexDirection}
        flexWrap={responsiveFlexWrap}
        justifyContent={responsiveJustifyContent}
        alignItems={responsiveAlignItems}
        flexGrow={responsiveFlexGrow}
        inset={responsiveInset}
        insetSquish={responsiveInsetSquish}
        insetStretch={responsiveInsetStretch}
        paddingX={responsivePaddingX}
        paddingY={responsivePaddingY}
        paddingTop={responsivePaddingTop}
        paddingBottom={responsivePaddingBottom}
        paddingLeft={responsivePaddingLeft}
        paddingRight={responsivePaddingRight}
        margin={responsiveMargin}
        marginX={responsiveMarginX}
        marginY={responsiveMarginY}
        marginTop={responsiveMarginTop}
        marginBottom={responsiveMarginBottom}
        marginLeft={responsiveMarginLeft}
        marginRight={responsiveMarginRight}
        overflow={responsiveOverflow}
        top={responsiveTop}
        bottom={responsiveBottom}
        right={responsiveRight}
        left={responsiveLeft}
        transform={responsiveTransform}
        {...restProps}
      >
        {children}
      </StyledDiv>
    )
  }
)

const StyledDiv = styled('div')<BoxStyleProps>((p) => {
  let boxStyles: Record<string, any> = {
    display: p.display,
    position: p.position,
    top: p.top,
    bottom: p.bottom,
    left: p.left,
    right: p.right,
    flexDirection: p.flexDirection,
    alignItems: p.alignItems && resolveFlexAlign(p.alignItems),
    justifyContent: p.justifyContent && resolveFlexJustify(p.justifyContent),
    flexWrap: p.flexWrap,
    flexGrow: p.flexGrow,
    background: p.background,
    opacity: p.opacity,
    border: p.border,
    borderRadius: p.radius && `var(--border-radius--${p.radius})`,
    pointerEvents: p.pointerEvents,
    zIndex: p.zIndex,
    transition: p.transition,
    transform: p.transform,
    overflow: p.overflow,
    cursor: p.cursor,
    userSelect: p.userSelect,
    color: p.color
  }

  if (p.height) boxStyles.height = resolveToPxIfUnitless(p.height)
  if (p.minHeight) boxStyles.minHeight = resolveToPxIfUnitless(p.minHeight)
  if (p.maxHeight) boxStyles.maxHeight = resolveToPxIfUnitless(p.maxHeight)
  if (p.width) boxStyles.width = resolveToPxIfUnitless(p.width)
  if (p.maxWidth) boxStyles.maxWidth = resolveToPxIfUnitless(p.maxWidth)
  if (p.minWidth) boxStyles.minWidth = resolveToPxIfUnitless(p.minWidth)

  const resolvedPaddingTop = p.paddingTop || p.paddingY || p.inset
  const resolvedPaddingBottom = p.paddingBottom || p.paddingY || p.inset
  const resolvedPaddingLeft = p.paddingLeft || p.paddingX || p.inset
  const resolvedPaddingRight = p.paddingRight || p.paddingX || p.inset

  let resolvedPadding
  if (p.insetSquish) {
    resolvedPadding = `var(--space-inset-squish--${p.insetSquish})`
  }

  if (p.insetStretch) {
    resolvedPadding = `var(--space-inset-stretch--${p.insetStretch})`
  }

  const resolvedMarginTop = p.marginTop || p.marginY || p.margin
  const resolvedMarginBottom = p.marginBottom || p.marginY || p.margin
  const resolvedMarginLeft = p.marginLeft || p.marginX || p.margin
  const resolvedMarginRight = p.marginRight || p.marginX || p.margin

  if (p.hidden) {
    boxStyles.visibility = 'hidden'
  }

  boxStyles = {
    ...boxStyles,

    marginTop: resolvedMarginTop && `var(--space--${resolvedMarginTop})`,
    marginBottom: resolvedMarginBottom && `var(--space--${resolvedMarginBottom})`,
    marginLeft: resolvedMarginLeft && `var(--space--${resolvedMarginLeft})`,
    marginRight: resolvedMarginRight && `var(--space--${resolvedMarginRight})`,

    paddingTop: resolvedPaddingTop && `var(--space--${resolvedPaddingTop})`,
    paddingBottom: resolvedPaddingBottom && `var(--space--${resolvedPaddingBottom})`,
    paddingLeft: resolvedPaddingLeft && `var(--space--${resolvedPaddingLeft})`,
    paddingRight: resolvedPaddingRight && `var(--space--${resolvedPaddingRight})`,

    padding: resolvedPadding
  }

  if (p.negativeMarginTop) {
    boxStyles.marginTop = 0
    boxStyles.paddingTop = '1px'
    boxStyles[':before'] = resolveNegativeMarginTop(p.negativeMarginTop)
  }

  if (p.negativeMarginLeft) {
    boxStyles.marginLeft = resolveNegativeMarginLeft(p.negativeMarginLeft)
  }

  return boxStyles
})

BoxComponent.displayName = 'Box'

export const Box = BoxComponent
